import { type User } from '.';
import Agency from './Agency';
import Customer from './customer';
import {
  BaseEntity,
  JsonApiProperty,
  JsonApiRelationship,
  JsonApiResource,
} from '../jsonapi-provider';
import type InvoiceConfiguration from './billing/invoice-configuration';
import type PriceAdjustment from './billing/price-adjustment';
import type Route from './route';
import type SiteMenuBeverageOrder from './site-menu-beverage-order';
import type SiteMenuOrder from './site-menu-order';
import type SiteMenuSupplyOrder from './site-menu-supply-order';

@JsonApiResource({
  type: 'site',
})
export default class Site extends BaseEntity {
  name: string = '';
  primaryAddress: string = '';
  secondaryAddress: string = '';
  city: string = '';
  custSiteNumber: string = '';
  primaryDeliveryAddress: string = '';
  secondaryDeliveryAddress: string = '';
  deliveryCity: string = '';
  deliveryState: string = '';
  deliveryZip: string = '';
  state: string = '';
  zip: string = '';
  county: string = '';

  @JsonApiProperty({ resolver: Number }) stopNumber?: number = undefined;
  @JsonApiProperty({ resolver: Number }) hhcRoute?: number = undefined;
  @JsonApiProperty({ resolver: Number }) invoiceOrder?: number = undefined;
  arrivalTime?: string = undefined;
  siteNumber?: string = undefined;
  isActive: boolean = true;

  @JsonApiRelationship('customer') customer = new Customer();
  @JsonApiRelationship('route') routes = new Array<Route>();
  @JsonApiRelationship('site-menu-order') siteMenuOrders? = new Array<SiteMenuOrder>();
  @JsonApiRelationship('site-menu-beverage-order') siteMenuBeverageOrders? =
    new Array<SiteMenuBeverageOrder>();

  @JsonApiRelationship('user') assignedUsers? = new Array<User>();

  @JsonApiRelationship('agency') agency = new Agency();
  @JsonApiRelationship('site-menu-supply-order') siteMenuSupplyOrders? =
    new Array<SiteMenuSupplyOrder>();

  @JsonApiRelationship('price-adjustment') priceAdjustments: PriceAdjustment[];
  @JsonApiRelationship('invoice-configuration') invoiceConfiguration:
    | InvoiceConfiguration
    | undefined;

  constructor(
    id?: Site['id'],
    name: Site['name'] = '',
    primaryAddress: Site['primaryAddress'] = '',
    secondaryAddress: Site['secondaryAddress'] = '',
    arrivalTime: Site['arrivalTime'] = undefined,
    city: Site['city'] = '',
    custSiteNumber: Site['custSiteNumber'] = '',
    primaryDeliveryAddress: Site['primaryDeliveryAddress'] = '',
    secondaryDeliveryAddress: Site['secondaryDeliveryAddress'] = '',
    deliveryCity: Site['deliveryCity'] = '',
    deliveryState: Site['deliveryState'] = '',
    deliveryZip: Site['deliveryZip'] = '',
    invoiceOrder: Site['invoiceOrder'] = undefined,
    hhcRoute: Site['hhcRoute'] = undefined,
    siteNumber: Site['siteNumber'] = undefined,
    state: Site['state'] = '',
    stopNumber: Site['stopNumber'] = undefined,
    zip: Site['zip'] = '',
    isActive: Site['isActive'] = true,
    customer: Site['customer'] = new Customer(),
    routes: Site['routes'] = new Array<Route>(),
    county: Site['county'] = '',
    agency: Site['agency'] = new Agency(),
    siteMenuOrders: Site['siteMenuOrders'] = new Array<SiteMenuOrder>(),
    siteMenuBeverageOrders: Site['siteMenuBeverageOrders'] = new Array<SiteMenuBeverageOrder>(),
    siteMenuSupplyOrders: Site['siteMenuSupplyOrders'] = new Array<SiteMenuSupplyOrder>(),
    priceAdjustments: Site['priceAdjustments'] = new Array<PriceAdjustment>(),
    invoiceConfiguration: Site['invoiceConfiguration'] = undefined
  ) {
    super();
    this.id = id;
    this.name = name;
    this.primaryAddress = primaryAddress;
    this.secondaryAddress = secondaryAddress;
    this.arrivalTime = arrivalTime;
    this.city = city;
    this.custSiteNumber = custSiteNumber;
    this.primaryDeliveryAddress = primaryDeliveryAddress;
    this.secondaryDeliveryAddress = secondaryDeliveryAddress;
    this.deliveryCity = deliveryCity;
    this.deliveryState = deliveryState;
    this.deliveryZip = deliveryZip;
    this.invoiceOrder = invoiceOrder;
    this.hhcRoute = hhcRoute;
    this.siteNumber = siteNumber;
    this.state = state;
    this.stopNumber = stopNumber;
    this.zip = zip;
    this.customer = customer;
    this.routes = routes;
    this.agency = agency;
    this.county = county;
    this.siteMenuOrders = siteMenuOrders;
    this.siteMenuBeverageOrders = siteMenuBeverageOrders;
    this.siteMenuSupplyOrders = siteMenuSupplyOrders;
    this.priceAdjustments = priceAdjustments;
    this.invoiceConfiguration = invoiceConfiguration;
    this.isActive = isActive;
  }
}

export interface SiteWithoutRelationships
  extends Omit<
    Site,
    | 'routes'
    | 'customer'
    | 'agency'
    | 'siteMenuOrders'
    | 'siteMenuBeverageOrders'
    | 'siteMenuSupplyOrders'
    | 'priceAdjustments'
    | 'invoiceConfiguration'
  > {}

export const toSiteWithoutRelationships = (site: Site): SiteWithoutRelationships => {
  return {
    id: site?.id,
    name: site?.name,
    primaryAddress: site?.primaryAddress,
    secondaryAddress: site?.secondaryAddress,
    arrivalTime: site?.arrivalTime,
    city: site?.city,
    county: site?.county,
    custSiteNumber: site?.custSiteNumber,
    primaryDeliveryAddress: site?.primaryDeliveryAddress,
    secondaryDeliveryAddress: site?.secondaryDeliveryAddress,
    deliveryCity: site?.deliveryCity,
    deliveryState: site?.deliveryState,
    deliveryZip: site?.deliveryZip,
    invoiceOrder: site?.invoiceOrder,
    hhcRoute: site?.hhcRoute,
    siteNumber: site?.siteNumber,
    state: site?.state,
    stopNumber: site?.stopNumber,
    zip: site?.zip,
    isActive: site?.isActive,
  };
};
